import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LegalHRPDFExport from '../../components/legal-hr/LegalHRPDFExport';
import toast from 'react-hot-toast';
import { anthropicService } from '../../services/anthropicService';
import { supabase } from '../../lib/supabase';
import { legalHrStorageService, SavedLegalHrDocument } from '../../services/legalHrStorageService';

// Define supported countries for HR policies
const supportedCountries = [
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'EU', name: 'European Union', flag: '🇪🇺' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
];

// Define business types for dropdown
const businessTypes = [
  'Technology Company',
  'Retail Business',
  'Professional Services',
  'Healthcare Organization',
  'Manufacturing Company',
  'Hospitality Business',
  'Educational Institution',
  'Non-profit Organization',
  'Financial Services',
  'Creative Agency',
];

// Define company sizes
const companySizes = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-500 employees',
  '501-1000 employees',
  '1000+ employees'
];

// Define HR policy types
const policyTypes = [
  { 
    id: 'attendance',
    label: 'Attendance & Time Off',
    example: 'Work hours, holidays, sick days',
    required: false,
    defaultSelected: true
  },
  { 
    id: 'code_of_conduct',
    label: 'Code of Conduct',
    example: 'Professional behavior, ethics, and values',
    required: true,
    defaultSelected: true
  },
  { 
    id: 'remote_work',
    label: 'Remote Work Policy',
    example: 'Guidelines for working from home or remotely',
    required: false,
    defaultSelected: false
  },
  {
    id: 'diversity',
    label: 'Diversity & Inclusion',
    example: 'Commitment to diversity and inclusion practices',
    required: false,
    defaultSelected: true
  },
  {
    id: 'performance',
    label: 'Performance Management',
    example: 'Evaluations, feedback, improvement plans',
    required: false,
    defaultSelected: false
  },
  {
    id: 'health_safety',
    label: 'Health & Safety',
    example: 'Workplace safety procedures and policies',
    required: true,
    defaultSelected: true
  },
  {
    id: 'harassment',
    label: 'Anti-Harassment & Discrimination',
    example: 'Zero tolerance for harassment or discrimination',
    required: true,
    defaultSelected: true
  },
  {
    id: 'communication',
    label: 'Communication Policy',
    example: 'Guidelines for internal and external communication',
    required: false,
    defaultSelected: false
  },
  {
    id: 'confidentiality',
    label: 'Confidentiality & Data Protection',
    example: 'Handling of sensitive information',
    required: true,
    defaultSelected: true
  },
  {
    id: 'disciplinary',
    label: 'Disciplinary Procedures',
    example: 'Steps for addressing misconduct',
    required: false,
    defaultSelected: false
  },
  {
    id: 'benefits',
    label: 'Benefits & Perks',
    example: 'Description of available employee benefits',
    required: false,
    defaultSelected: false
  },
  {
    id: 'leave',
    label: 'Leave Management',
    example: 'Maternity, paternity, bereavement leave',
    required: false,
    defaultSelected: true
  },
  {
    id: 'employee_devices',
    label: 'Employee Devices & Technology',
    example: 'Usage of company equipment and technology',
    required: false,
    defaultSelected: false
  },
  {
    id: 'social_media',
    label: 'Social Media Policy',
    example: 'Guidelines for professional social media use',
    required: false,
    defaultSelected: false
  }
];

const HrPolicyGenerator: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [policyGenerated, setPolicyGenerated] = useState(false);
  
  // Form state
  const [companyName, setCompanyName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [hasHrDepartment, setHasHrDepartment] = useState<boolean | null>(null);
  const [selectedPolicies, setSelectedPolicies] = useState<string[]>(
    policyTypes.filter(policy => policy.defaultSelected || policy.required).map(policy => policy.id)
  );
  const [selectedCountry, setSelectedCountry] = useState('GB');
  const [workplaceType, setWorkplaceType] = useState('hybrid');
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [policyText, setPolicyText] = useState('');
  const [latestDocument, setLatestDocument] = useState<SavedLegalHrDocument | null>(null);

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const handlePolicyChange = (policyId: string) => {
    // Don't allow deselection of required policies
    if (policyTypes.find(policy => policy.id === policyId)?.required) {
      return;
    }
    
    if (selectedPolicies.includes(policyId)) {
      setSelectedPolicies(selectedPolicies.filter(item => item !== policyId));
    } else {
      setSelectedPolicies([...selectedPolicies, policyId]);
    }
  };

  const generatePolicy = async () => {
    // Validation
    if (!companyName) {
      toast.error('Please enter company name');
      return;
    }
    
    if (!businessType) {
      toast.error('Please select your business type');
      return;
    }
    
    if (!companySize) {
      toast.error('Please select your company size');
      return;
    }
    
    if (!companyDescription) {
      toast.error('Please enter a company description');
      return;
    }
    
    if (hasHrDepartment === null) {
      toast.error('Please specify if your company has an HR department');
      return;
    }

    setGenerating(true);
    
    try {
      // Get the country name from the selected country code
      const selectedCountryData = supportedCountries.find(country => country.code === selectedCountry);
      const countryName = selectedCountryData ? selectedCountryData.name : 'United Kingdom';
      
      // Get current user ID from Supabase
      const { data } = await supabase.auth.getUser();
      const userId = data.user?.id;
      
      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }

      // Format the selected policies into a readable list
      const selectedPolicyTypes = policyTypes
        .filter(policy => selectedPolicies.includes(policy.id))
        .map(policy => policy.label)
        .join(', ');

      // Determine workplace type text
      let workplaceTypeText = '';
      switch (workplaceType) {
        case 'inOffice':
          workplaceTypeText = 'in-office (traditional workplace)';
          break;
        case 'remote':
          workplaceTypeText = 'fully remote';
          break;
        case 'hybrid':
          workplaceTypeText = 'hybrid (mix of remote and in-office)';
          break;
      }

      // Create a comprehensive prompt for Anthropic
      const prompt = `
Generate a comprehensive set of HR policies for "${companyName}", a ${businessType} with ${companySize} that is primarily ${workplaceTypeText}.

About the company: ${companyDescription}

The company ${hasHrDepartment ? 'has' : 'does not have'} a dedicated HR department.

The HR policies should be compliant with employment laws and regulations in ${countryName} and include the following sections: ${selectedPolicyTypes}

Effective date for these policies: ${effectiveDate}

For each policy, include:
1. A clear policy title and introduction explaining its purpose
2. Specific guidelines and expectations
3. Any relevant procedures or processes
4. Consequences for policy violations (where applicable)

Include appropriate legal language referencing specific ${countryName} employment laws such as:
${
  selectedCountry === 'GB' ? 'UK Employment Rights Act, Equality Act 2010, Working Time Regulations, GDPR' : 
  selectedCountry === 'US' ? 'Fair Labor Standards Act, Equal Employment Opportunity laws, OSHA regulations' : 
  selectedCountry === 'EU' ? 'EU Working Time Directive, GDPR, EU equality directives' : 
  selectedCountry === 'CA' ? 'Canada Labour Code, provincial employment standards acts, human rights legislation' : 
  selectedCountry === 'AU' ? 'Fair Work Act, workplace health and safety regulations, privacy laws' : 
  'relevant employment and labor laws in your jurisdiction'
}

Format the policies in professional, clear language appropriate for employee handbooks. Use markdown formatting for the document with proper headings and structure.
`;

      // Make the actual API call to Anthropic through our service
      const response = await anthropicService.generateText(prompt);
      
      setPolicyText(response);
      setLatestDocument(null);
      setPolicyGenerated(true);
      toast.success('HR Policy generated successfully!');
    } catch (error) {
      console.error('Error generating HR policy:', error);
      toast.error('Failed to generate HR policy. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (policyText) {
      navigator.clipboard.writeText(policyText)
        .then(() => toast.success('HR Policy copied to clipboard'))
        .catch((err) => {
          console.error('Failed to copy:', err);
          toast.error('Failed to copy text');
        });
    } else {
      toast.error('No content to copy');
    }
  };

  const handlePdfExportComplete = async ({ blob }: { filename: string; blob: Blob }) => {
    if (!user?.id) {
      toast.error('You must be logged in to save the policies');
      return;
    }

    try {
      const saved = await legalHrStorageService.saveLegalHrDocument({
        userId: user.id,
        docType: 'hr-policy',
        blob,
        baseName: companyName || 'hr-policy',
        effectiveDate,
        metadata: {
          companyName,
          businessType,
          companySize,
          companyDescription,
          hasHrDepartment,
          workplaceType,
          selectedPolicies,
          jurisdiction: selectedCountry,
        },
        tags: [
          `jurisdiction:${selectedCountry}`,
          companySize ? `company-size:${companySize.replace(/\s+/g, '-').toLowerCase()}` : 'company-size:unspecified',
          `workplace-type:${workplaceType}`,
          `hr-department:${hasHrDepartment ? 'yes' : 'no'}`,
        ],
      });

      setLatestDocument(saved);
      toast.success(`HR Policy saved to workspace as version v${saved.version}`);
    } catch (error) {
      console.error('Failed to upload HR Policy PDF', error);
      toast.error('Failed to save HR Policy to storage');
    }
  };



  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header with gradient */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-indigo-600">
            HR Policy Generator
          </h1>
          <p className="mt-3 text-xl text-gray-600 max-w-2xl mx-auto">
            Create comprehensive HR policies tailored to your organization and compliant with your jurisdiction.
          </p>
        </div>

        {/* Main content */}
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {!policyGenerated ? (
            <div className="p-6 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column - Company Info */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Company Information</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name*
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Your Company Ltd"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-1">
                        Business Type*
                      </label>
                      <select
                        id="businessType"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        value={businessType}
                        onChange={(e) => setBusinessType(e.target.value)}
                      >
                        <option value="">Select a business type</option>
                        {businessTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="companySize" className="block text-sm font-medium text-gray-700 mb-1">
                        Company Size*
                      </label>
                      <select
                        id="companySize"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        value={companySize}
                        onChange={(e) => setCompanySize(e.target.value)}
                      >
                        <option value="">Select company size</option>
                        {companySizes.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="companyDescription" className="block text-sm font-medium text-gray-700 mb-1">
                        Company Description*
                      </label>
                      <textarea
                        id="companyDescription"
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Describe your company's mission, values, and business activities"
                        value={companyDescription}
                        onChange={(e) => setCompanyDescription(e.target.value)}
                      ></textarea>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Does your company have an HR department?*
                      </label>
                      <div className="flex space-x-4">
                        <button
                          type="button"
                          onClick={() => setHasHrDepartment(true)}
                          className={`px-4 py-2 rounded-md ${
                            hasHrDepartment === true
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => setHasHrDepartment(false)}
                          className={`px-4 py-2 rounded-md ${
                            hasHrDepartment === false
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          No
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Primary Workplace Type
                      </label>
                      <div className="flex flex-col space-y-2">
                        <div className="flex items-center">
                          <input
                            id="inOffice"
                            name="workplaceType"
                            type="radio"
                            checked={workplaceType === 'inOffice'}
                            onChange={() => setWorkplaceType('inOffice')}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                          />
                          <label htmlFor="inOffice" className="ml-2 block text-sm text-gray-700">
                            In-Office (Traditional)
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="remote"
                            name="workplaceType"
                            type="radio"
                            checked={workplaceType === 'remote'}
                            onChange={() => setWorkplaceType('remote')}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                          />
                          <label htmlFor="remote" className="ml-2 block text-sm text-gray-700">
                            Fully Remote
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="hybrid"
                            name="workplaceType"
                            type="radio"
                            checked={workplaceType === 'hybrid'}
                            onChange={() => setWorkplaceType('hybrid')}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                          />
                          <label htmlFor="hybrid" className="ml-2 block text-sm text-gray-700">
                            Hybrid (Mix of Remote & In-Office)
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right column - Policy Details */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-800">Policy Details</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                        Jurisdiction*
                      </label>
                      <select
                        id="country"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                      >
                        {supportedCountries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-sm text-gray-500">
                        Ensures your policies comply with local employment laws
                      </p>
                    </div>

                    <div>
                      <label htmlFor="effectiveDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Effective Date
                      </label>
                      <input
                        type="date"
                        id="effectiveDate"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        value={effectiveDate}
                        onChange={(e) => setEffectiveDate(e.target.value)}
                      />
                    </div>

                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Select policies to include:
                      </label>
                      <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                        {policyTypes.map((policy) => (
                          <div key={policy.id} className="flex items-start">
                            <div className="flex items-center h-5">
                              <input
                                id={policy.id}
                                type="checkbox"
                                className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                checked={selectedPolicies.includes(policy.id)}
                                onChange={() => handlePolicyChange(policy.id)}
                                disabled={policy.required}
                              />
                            </div>
                            <div className="ml-3 text-sm">
                              <label htmlFor={policy.id} className="font-medium text-gray-700 flex items-center">
                                {policy.label}
                                {policy.required && (
                                  <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-800 text-xs rounded">
                                    Required
                                  </span>
                                )}
                              </label>
                              <p className="text-gray-500">{policy.example}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Generate button */}
              <div className="mt-8 flex justify-center">
                <button
                  onClick={generatePolicy}
                  disabled={generating}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-medium rounded-md shadow-md hover:shadow-lg transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    'Generate HR Policies'
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="p-6 sm:p-8">
              <div className="mb-6 flex justify-between">
                <h2 className="text-xl font-semibold text-gray-800">Generated HR Policies</h2>
                <div className="space-x-2">
                  <button 
                    onClick={handleCopyToClipboard}
                    className="px-3 py-2 bg-gray-100 text-gray-600 font-medium rounded-md shadow hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Copy to Clipboard
                  </button>
                  {policyText && (
                    <LegalHRPDFExport 
                      contentRef={contentRef} 
                      filename="hr-policy.pdf"
                      title={companyName ? `${companyName} - HR Policy` : "HR Policy"}
                      documentType="HR Policy Document"
                      onExportComplete={handlePdfExportComplete}
                    />
                  )}
                  <button 
                    onClick={() => setPolicyGenerated(false)}
                    className="px-3 py-2 bg-gray-100 text-gray-600 font-medium rounded-md shadow hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Edit
                  </button>
                </div>
              </div>
              
              {/* Display the generated policy in a nicely formatted way */}
              <div className="bg-white p-6 rounded-lg shadow-md" ref={contentRef}>
                <div dangerouslySetInnerHTML={{ __html: policyText }} />
              </div>
              

              
              <div className="mt-8 text-center">
                {latestDocument?.signedUrl && (
                  <div className="mb-4">
                    <a
                      href={latestDocument.signedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 underline"
                    >
                      View saved HR Policy (v{latestDocument.version})
                    </a>
                  </div>
                )}
                <div className="bg-yellow-50 p-4 border border-yellow-100 rounded-md mb-4">
                  <p className="text-sm text-yellow-700">
                    <strong>Legal Disclaimer:</strong> These HR policies are generated by AI and should be reviewed by a qualified HR professional or legal counsel before implementation. Employment laws vary by jurisdiction and may change over time. The generated policies may not cover all legal requirements specific to your business or jurisdiction.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setPolicyGenerated(false);
                    window.scrollTo(0, 0);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-medium rounded-md shadow-md hover:shadow-lg transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Generate New Policies
                </button>
              </div>
            </div>
          )}
          
          {/* Disclaimer */}
          <div className="bg-yellow-50 p-4 border-t border-yellow-100">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Legal Disclaimer</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    This generator provides HR policy templates only and is not a substitute for professional HR or legal advice. 
                    The generated policies should be reviewed by qualified HR professionals or legal counsel before implementation.
                    Proper HR policies are critical to maintain legal compliance and protect both employer and employees.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HrPolicyGenerator;
